import {
  memo, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useAtlas } from './atlas';
import { CLIPS, CLIP_FALLBACK, frameAt } from './clips';
import { buffyScale } from '../hero/layout';
import { canvasStyle, createPainter } from './painter';
import { addSnapTarget } from './pixelSnap';
import './buffy.css';
import { withBuffyGate } from './buffyOff';

/* BuffySprite: the reusable Buffy player for anywhere outside the hero (the
   page sections, demos). Built on the hero's registry (buffy/clips.js), the
   same pre-decoded atlas (buffy/atlas.js) and the same painter (a <canvas>
   at a whole number of DEVICE px per art pixel, painter.js), so he is exactly
   as crisp as in the hero at every display scaling, and a clip change can
   never flash a blank frame. His box is snapped to the device-pixel grid
   (pixelSnap.js). Playback runs from the clip's own timing (frameAt) on
   requestAnimationFrame, and paints only when the frame changes.

   The element keeps the NOMINAL size (64 x scale CSS px) for layout; the
   painted cell (64 x dev / devicePixelRatio) sits on the same feet anchor
   (bottom center), so nothing around him moves at any display scaling.

   Props:
     clip     registry name ('wave', 'celebrate', 'idle-breathe', 'look', ...).
              A missing name plays its CLIP_FALLBACK (rest).
     loop     true = repeat; defaults to the clip's own playback (loops for
              idle-breathe and listen, once for gestures).
     scale    integer 1-4 (nominal cell = 64 x scale px). Default 2.
     mirror   flip horizontally (legal: the base pose is symmetrical).
     paused   freeze on the current frame.
     onEnd    called when a once-clip finishes (chain the next clip here;
              every gesture starts and ends on the rest pose, so chaining never
              cuts).
     run      bump to replay the same clip.
     pose     for 'look': 'LEFT' | 'RIGHT' | 'UP' | 'DOWN' (a held pose).
     trim     true = skip the clip's rest lead-in (starts 100 ms before the
              first motion frame, like the hero).
     label    accessible name; omit for decorative (aria-hidden).
     className, style
   Example: <BuffySprite clip={clip} scale={3} onEnd={() => setClip(next)} /> */

function resolve(name) {
  if (CLIPS[name]) return CLIPS[name];
  return CLIPS[CLIP_FALLBACK[name]] || CLIPS.rest;
}

const currentDpr = () => (typeof window !== 'undefined' && window.devicePixelRatio) || 1;

/* The devicePixelRatio, kept current (browser zoom, another screen). */
function useDpr() {
  const [dpr, setDpr] = useState(currentDpr);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    let mq = null;
    const onChange = () => {
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onChange);
      setDpr(currentDpr());
      watch();
    };
    function watch() {
      mq = window.matchMedia(`(resolution: ${currentDpr()}dppx)`);
      if (mq.addEventListener) mq.addEventListener('change', onChange);
    }
    watch();
    window.addEventListener('resize', onChange);
    return () => {
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);
  return dpr;
}

function BuffySprite({
  clip = 'rest', loop, scale = 2, mirror = false, paused = false, onEnd, run = 0, pose, trim = false,
  label, className = '', style,
}) {
  const atlas = useAtlas();
  const dpr = useDpr();
  const c = resolve(clip);
  const s = Math.max(1, Math.min(4, Math.round(scale)));
  const nominal = 64 * s;
  const { dev, u } = buffyScale(s, dpr);
  const css = 64 * u;
  const isLoop = loop ?? c.playback === 'loop';

  const cellRef = useRef(null);
  const canvasRef = useRef(null);
  const painterRef = useRef(null);
  const endRef = useRef(onEnd);
  endRef.current = onEnd;
  // playback clock: ms of clip time already played (survives pauses)
  const clock = useRef({ played: 0 });

  // the painter, sized to the device scale
  useLayoutEffect(() => {
    if (!painterRef.current) painterRef.current = createPainter(canvasRef.current);
    painterRef.current.scale(dev, dpr);
    painterRef.current.invalidate();
  }, [dev, dpr]);

  // a new clip (or a replay) starts from its beginning
  useLayoutEffect(() => {
    clock.current = { played: trim && c.trimIn ? c.trimIn : 0 };
  }, [c.name, run, trim, c.trimIn]);

  // playback
  useEffect(() => {
    const painter = painterRef.current;
    if (!painter) return undefined;
    const once = !isLoop && c.playback === 'once';
    const frameNow = () => {
      const local = clock.current.played;
      if (c.playback === 'once' && isLoop && c.durationMs > 0) return frameAt({ ...c, playback: 'loop' }, local, pose);
      return frameAt(c, local, pose);
    };
    painter.draw(c.name, frameNow(), mirror);
    if (paused || c.playback === 'hold' || c.playback === 'poses') return undefined;
    if (once && clock.current.played >= c.durationMs) return undefined;
    let raf = 0;
    let last = null;
    const tick = (now) => {
      if (last !== null) clock.current.played += Math.min(now - last, 100);
      last = now;
      painter.draw(c.name, frameNow(), mirror);
      if (once && clock.current.played >= c.durationMs) {
        if (endRef.current) endRef.current(c.name);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [c, run, isLoop, paused, pose, mirror, atlas, dev]);

  // the painted cell on the device-pixel grid
  useLayoutEffect(() => {
    const el = cellRef.current;
    if (!el) return undefined;
    let sx = 0;
    let sy = 0;
    return addSnapTarget({
      origin() {
        if (!el.isConnected) return null;
        const r = el.getBoundingClientRect();
        return { x: r.left - sx, y: r.top - sy };
      },
      apply(x, y) {
        if (x === sx && y === sy) return;
        sx = x;
        sy = y;
        el.style.translate = x || y ? `${x}px ${y}px` : '';
      },
    });
  }, []);

  return (
    <span
      className={`bx-player ${className}`}
      style={{ width: nominal, height: nominal, ...style }}
      role={label ? 'img' : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : 'true'}
      data-clip={c.name}
    >
      <span
        ref={cellRef}
        className="bx-player-cell"
        style={{ left: (nominal - css) / 2, top: nominal - css, width: css, height: css }}
      >
        <canvas ref={canvasRef} className="bx-canvas" width={64 * dev} height={64 * dev} style={canvasStyle(dev, dpr)} />
      </span>
    </span>
  );
}

// a page can take Buffy off (buffyOff.js); everywhere else this is the sprite
export default withBuffyGate(memo(BuffySprite));
